import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRight, Check, CircleAlert, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusPill } from '@/components/status-pill'
import { formatMoney, parseUTCDate } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import type { QuoteWorkflow } from '../api/types'
import { projectStartQueryOptions, type QuoteProject } from './api'

const ROLES = [
  ['sales_person', 'Sales'],
  ['ops_person', 'Ops'],
  ['warehousing_person', 'Warehouse'],
  ['accounting_person', 'Accounting'],
] as const

/** Where the project's PO review stands (USA-1333); the POs themselves are on the project. */
function PoReview({ project }: { project: QuoteProject }) {
  const projectId = project.project.uuid ?? ''
  if (project.placement === 'cutting') {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <LoaderCircle aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
        Creating {project.expected_purchase_orders} POs
      </p>
    )
  }
  if (project.po_review === 'placed') {
    return (
      <p className="text-sm">
        Reviewed and sent
        {project.pos_placed_by?.name ? ` by ${project.pos_placed_by.name}` : ''}
        {project.pos_placed_at ? (
          <span className="text-muted-foreground"> · {parseUTCDate(project.pos_placed_at).toLocaleString()}</span>
        ) : null}
      </p>
    )
  }
  if (project.po_review !== 'waiting') return null
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {project.placement === 'needs_attention' ? (
        <p className="flex gap-2 text-sm text-amber-800 dark:text-amber-200" role="alert">
          <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          Some POs could not be created.
        </p>
      ) : (
        <p className="text-sm tabular-nums">
          {project.pos_reviewed} of {project.pos_to_review} reviewed
        </p>
      )}
      <Button asChild size="sm" variant="outline">
        <Link params={{ projectId }} to="/projects/$projectId">
          Review POs
          <ArrowRight aria-hidden="true" />
        </Link>
      </Button>
    </div>
  )
}

/**
 * The workspace once the project started (demo `StartedState`): the project, its order, where its
 * PO review stands, and who the project went to.
 */
export function StartedProject({ quoteId, workflow }: { quoteId: string; workflow: QuoteWorkflow }) {
  const api = useQuoteBuilderApi()
  const { data } = useQuery(projectStartQueryOptions(api, quoteId))
  const project = data?.project

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-4 sm:px-5">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-emerald-600 text-white">
          <Check aria-hidden="true" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold tracking-tight">Project started</h2>
          {workflow.started_at ? (
            <p className="text-sm text-muted-foreground">
              {parseUTCDate(workflow.started_at).toLocaleString()}
              {/* Since USA-1334 a project starts on its own; before, Sales started it. */}
              {project && !project.po_review ? ` by ${project.started_by.name ?? 'staff'}` : null}
            </p>
          ) : null}
        </div>
      </header>

      {project ? (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 lg:border-r">
            <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 sm:px-5">
              <div className="min-w-0">
                <dt className="text-xs text-muted-foreground">Project</dt>
                <dd className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                  <Link
                    className="truncate underline-offset-4 hover:underline"
                    params={{ projectId: project.project.uuid ?? '' }}
                    to="/projects/$projectId"
                  >
                    {project.project.name}
                  </Link>
                  {project.project_status ? (
                    <StatusPill tone={project.project_status.tone}>{project.project_status.label}</StatusPill>
                  ) : null}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs text-muted-foreground">Order</dt>
                <dd className="flex items-baseline gap-2 text-sm">
                  <Link
                    className="font-semibold tabular-nums underline-offset-4 hover:underline"
                    params={{ orderId: project.order.uuid ?? '' }}
                    to="/orders/$orderId"
                  >
                    #{project.order.order_number}
                  </Link>
                  {project.order.total != null ? (
                    <span className="tabular-nums text-muted-foreground">{formatMoney(project.order.total)}</span>
                  ) : null}
                </dd>
              </div>
            </dl>
            {project.po_review || project.placement === 'cutting' ? (
              <div className="grid gap-2 border-t px-4 py-3 sm:px-5">
                <h3 className="text-sm font-semibold">PO review</h3>
                <PoReview project={project} />
              </div>
            ) : null}
          </div>
          <div className="border-t px-4 py-4 sm:px-5 lg:border-t-0">
            <h3 className="text-sm font-semibold">Project team</h3>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-1">
              {ROLES.map(([role, label]) => (
                <div className="min-w-0" key={role}>
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="truncate text-sm">{project.project[role]?.name ?? 'Unassigned'}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 p-5">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
    </section>
  )
}
